'use client'

import * as Tabs from 'primitives/tabs'
import * as React from 'react'
import { CrafterTabs } from './crafter-tabs'
import { socialUserFeedQueryFetcher } from '@web/gen/feed'
import { useQuery } from '@tanstack/react-query'
import { CrafterFeed } from './crafter-feed'
import { AboutContent } from '../about/about-content'
import { SkeletonItem } from 'components/skeleton-item'
import { RightSidebar } from '../../../masterfeed/_components/right-sidebar'
import { FollowersContent } from '../followers/followers-content'
import { FollowingContent } from '../following/following-content'
import { usePathname } from 'next/navigation'

function PostsTabContent({ slug }: { slug: string }) {
  const { data: fetchedData, isLoading } = useQuery({
    queryKey: ['socialUserFeed', slug],
    queryFn: () =>
      socialUserFeedQueryFetcher({
        input: { userSlug: slug, itemsPerPage: 8 },
      })(),
  })

  if (isLoading || !fetchedData) {
    return (
      <div className="flex w-full">
        <SkeletonItem className="h-[800px] w-full rounded-[12px]" />
        <RightSidebar className="ml-6" />
      </div>
    )
  }

  return (
    <div className="flex w-full">
      <CrafterFeed slug={slug} initialData={fetchedData} />
      <RightSidebar showSidebar={!!fetchedData.socialUserFeed.posts?.length} className="ml-6" />
    </div>
  )
}

export function CrafterTabsContent({
  slug,
  userId,
  initialTab,
}: {
  slug: string
  userId: string
  initialTab: string
}) {
  const pathname = usePathname()

  // Extract active tab from URL
  const activeTab = React.useMemo(() => {
    const clean = pathname.replace(/\/$/, '')
    const parts = clean.split('/').filter(Boolean)
    const idx = parts.indexOf(slug)
    const next = parts[idx + 1]
    return ['posts', 'about', 'favorites', 'followers', 'following'].includes(next) ? next : 'posts'
  }, [pathname, slug])

  return (
    <CrafterTabs slug={slug} initialTab={initialTab}>
      <Tabs.Content value="posts" className="mt-4 sm:mt-5">
        {activeTab === 'posts' ? <PostsTabContent slug={slug} /> : null}
      </Tabs.Content>

      <Tabs.Content value="about" className="mt-4 sm:mt-5">
        {activeTab === 'about' ? <AboutContent slug={slug} /> : null}
      </Tabs.Content>

      <Tabs.Content value="followers" className="mt-4 sm:mt-5">
        {activeTab === 'followers' ? (
          <div className="flex w-full">
            <FollowersContent slug={slug} userId={userId} />
          </div>
        ) : null}
      </Tabs.Content>

      <Tabs.Content value="following" className="mt-4 sm:mt-5">
        {activeTab === 'following' ? (
          <div className="flex w-full">
            <FollowingContent slug={slug} userId={userId} />
          </div>
        ) : null}
      </Tabs.Content>
    </CrafterTabs>
  )
}
